// Typed client for bug/feature reports (owner decision, Jens, 2026-10-22).
//
// TWO AUDIENCES, TWO SETS OF CALLS:
//
//   REPORTERS — anybody signed in, where the deployment opens filing — send
//   ONE report and read nothing back: `submitBugReport` from the portal
//   (`POST /bugs/report`, the organization the portal acts in) and
//   `submitMemberBugReport` from the member app (`POST /member/bug-reports`,
//   the member's own club).
//
//   BUG ADMINISTRATORS — an allowlist of staff addresses — read every club's
//   reports (`fetchBugs`, `fetchBug`) and change one thing at a time
//   (`setBugStatus`, `addBugComment`, `deleteBug`). Rows are addressed by the
//   server's UUID, which is unique across clubs; the legacy `bug_…` id is
//   `legacyId`.
//
// The whole-collection write (`PUT /bugs`, «read the club's list, prepend,
// write it all back») is gone: it needed the authority to rewrite every report
// a club had in order to add one.
//
// OFF THE `http` BACKEND (memory / tests) everything falls back to the
// key/value Storage the app used before (`bugs_v1`), as one local list — there
// is no server to separate reporters from administrators, so the local
// behaviour is the old one. The status derivation the server owns is mirrored
// here for that path only.
import { Storage } from '../storage';
import { apiFetch } from './http';
import { REAUTHENTICATION_REQUIRED } from './reauthenticationSignal';

const KEY = 'bugs_v1';

/** A refused bug-report call: the server's code, for the browser to phrase. */
export class BugApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(`[bugs] ${code} (${status})`);
  }
}

async function refusal(res: Response): Promise<BugApiError> {
  let code = 'request_failed';
  try {
    const body = (await res.json()) as { error?: unknown } | null;
    if (typeof body?.error === 'string') code = body.error;
  } catch {
    /* not JSON — keep the generic code */
  }
  return new BugApiError(code, res.status);
}

async function readLocal(): Promise<any[]> {
  const r = await Storage.get(KEY);
  return r?.value ? JSON.parse(r.value) : [];
}
async function writeLocal(list: any[]): Promise<void> {
  await Storage.set(KEY, JSON.stringify(list));
}

/**
 * The administrator's session is too old to read the reports (#1141).
 *
 * THROWN, not answered as `[]`: an empty list would read as «no reports», and
 * the badge poll would keep asking every two seconds, re-opening the step-up
 * prompt each time it is dismissed. Callers pause on it and resume on
 * `onReauthenticated`.
 */
export class StaleAuthenticationError extends Error {
  constructor() {
    super('reauthentication_required');
    this.name = 'StaleAuthenticationError';
  }
}

/** Every club's reports, light (#878) — for a bug administrator. */
export async function fetchBugs(): Promise<any[]> {
  if (Storage.backend === 'http') {
    const res = await apiFetch('/bugs');
    if (!res.ok) {
      if (res.status === 403) {
        const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
        if (body?.error === REAUTHENTICATION_REQUIRED) throw new StaleAuthenticationError();
      }
      return [];
    }
    return (await res.json()) as any[];
  }
  return readLocal();
}

/**
 * ONE bug in full — `screenshot` and `consoleLog` included (#878).
 *
 * On the `http` backend the list (`fetchBugs`) does not carry those two: it is
 * what the ticket badges poll every 2 seconds. They are loaded here, per bug,
 * when one is opened. Off the http backend the list is still the whole
 * document, so this just picks the bug out of it.
 *
 * `null` when the bug is not there (deleted meanwhile, or the read failed).
 */
export async function fetchBug(id: string): Promise<any | null> {
  if (Storage.backend === 'http') {
    const res = await apiFetch(`/bugs/${encodeURIComponent(id)}`);
    if (!res.ok) return null;
    return (await res.json()) as any;
  }
  const list = await readLocal();
  return list.find((b) => b?.id === id) ?? null;
}

/**
 * What the reporter sends. The server keeps a whitelist of these and mints the
 * rest — the id, the time, the status and who reported it.
 */
export interface BugReportInput {
  kind: 'bug' | 'feature';
  title: string;
  description: string;
  page?: string | null;
  view?: string | null;
  context?: Record<string, unknown>;
  screenshot?: string | null;
  consoleLog?: string | null;
  actions?: unknown;
  failedRequests?: unknown;
  runtime?: unknown;
  userAgent?: string | null;
  url?: string | null;
}

/** Off the http backend: the report as the app always stored it, prepended. */
async function fileLocally(report: BugReportInput): Promise<{ id: string }> {
  const now = Date.now();
  const id = `${report.kind}_${now}_${Math.floor(Math.random() * 1000)}`;
  const bug = {
    ...report,
    id,
    createdAt: now,
    status: 'open',
    fixed: false,
    fixedAt: null,
    comments: [],
  };
  // STORAGE FIRST (CLAUDE.md): the caller updates its state after this resolves.
  await writeLocal([bug, ...(await readLocal())]);
  return { id };
}

async function post(path: string, report: BugReportInput): Promise<{ id: string }> {
  const res = await apiFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(report),
  });
  if (!res.ok) throw await refusal(res);
  return (await res.json()) as { id: string };
}

/** File ONE report from the portal, for the organization it is acting in. */
export async function submitBugReport(report: BugReportInput): Promise<{ id: string }> {
  if (Storage.backend === 'http') return post('/bugs/report', report);
  return fileLocally(report);
}

/** File ONE report from the member app, for the member's own club. */
export async function submitMemberBugReport(report: BugReportInput): Promise<{ id: string }> {
  if (Storage.backend === 'http') return post('/member/bug-reports', report);
  return fileLocally(report);
}

/** The fields a status change writes. */
export interface BugStatusChange {
  status: string;
  fixed: boolean;
  fixedAt: number | null;
  rejectedAt: number | null;
}

/**
 * The server's derivation (`statusChange` in `apps/api/src/routes/bugs.ts`),
 * mirrored for the local backend only. On `http` the server decides.
 */
export function deriveStatusChange(
  current: { fixedAt?: number | null; rejectedAt?: number | null },
  status: string,
  now: number = Date.now(),
): BugStatusChange {
  const ended = status === 'solved' || status === 'closed' || status === 'approved';
  let fixedAt = current.fixedAt ?? null;
  let rejectedAt = current.rejectedAt ?? null;
  if (ended && !fixedAt) fixedAt = now;
  if (status === 'rejected' && !rejectedAt) rejectedAt = now;
  if (status === 'open') {
    fixedAt = null;
    rejectedAt = null;
  }
  return { status, fixed: ended, fixedAt, rejectedAt };
}

/** Change one report's status. Answers the fields the change wrote. */
export async function setBugStatus(id: string, status: string): Promise<BugStatusChange> {
  if (Storage.backend === 'http') {
    const res = await apiFetch(`/bugs/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!res.ok) throw await refusal(res);
    const body = (await res.json()) as BugStatusChange;
    return {
      status: body.status,
      fixed: body.fixed,
      fixedAt: body.fixedAt ?? null,
      rejectedAt: body.rejectedAt ?? null,
    };
  }
  const list = await readLocal();
  const bug = list.find((b) => b?.id === id);
  if (!bug) throw new BugApiError('not_found', 404);
  const change = deriveStatusChange(bug, status);
  await writeLocal(list.map((b) => (b.id === id ? { ...b, ...change } : b)));
  return change;
}

/**
 * Add one comment. Answers the whole thread afterwards — including a legacy
 * `ownerComment` the server migrated into it on this first reply.
 */
export async function addBugComment(
  id: string,
  text: string,
  author: 'owner' | 'user' = 'owner',
): Promise<any[]> {
  if (Storage.backend === 'http') {
    const res = await apiFetch(`/bugs/${encodeURIComponent(id)}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, author }),
    });
    if (!res.ok) throw await refusal(res);
    return ((await res.json()) as { comments: any[] }).comments;
  }
  const list = await readLocal();
  const bug = list.find((b) => b?.id === id);
  if (!bug) throw new BugApiError('not_found', 404);
  const now = Date.now();
  const comment = {
    id: `c_${now}_${Math.floor(Math.random() * 10000)}`,
    text: text.trim(),
    author,
    createdAt: now,
  };
  const thread = Array.isArray(bug.comments) ? bug.comments : [];
  // The legacy owner remark becomes the thread's first entry on the first reply.
  const migrated =
    bug.ownerComment && thread.length === 0
      ? [
          {
            id: `legacy_${bug.id}`,
            text: bug.ownerComment,
            author: 'owner',
            createdAt: bug.ownerCommentAt || bug.createdAt,
          },
        ]
      : [];
  const comments = [...migrated, ...thread, comment];
  await writeLocal(list.map((b) => (b.id === id ? { ...b, comments } : b)));
  return comments;
}

/** Delete one report (its thread goes with it). */
export async function deleteBug(id: string): Promise<void> {
  if (Storage.backend === 'http') {
    const res = await apiFetch(`/bugs/${encodeURIComponent(id)}`, { method: 'DELETE' });
    if (!res.ok) throw await refusal(res);
    return;
  }
  const list = await readLocal();
  await writeLocal(list.filter((b) => b?.id !== id));
}
